import { titleize } from './inflection';

/**
 * Date Completed (`recorded_at`) — the label and the display fallback, mirrored
 * from the online app so GO and the Rails editor agree about this field.
 *
 * Label: monami calls `recorded_at` "Date Completed" in every editor surface
 * (`app/views/admin/org/documents/_document.html.haml`, the processors form,
 * and the shared `DateCompletedInput`). The raw attribute name only ever
 * appears in a server validation message, so anything that renders one has to
 * translate it back — see `humanizeFieldName`.
 *
 * The range rule is NOT here: `DateCompletedInput` owns the field's min/max
 * window and rejects an out-of-range value before it reaches this app
 * (@mes-amis/super-form >= 1.2.45).
 */

/** The label every Mon Ami surface uses for `recorded_at`. */
export const DATE_COMPLETED_LABEL = 'Date Completed';

/**
 * Server attribute name → the label the user actually sees. Only fields that
 * can appear in a rejected document PATCH need an entry; anything else falls
 * back to a title-cased version of the attribute name.
 */
const FIELD_LABELS: Record<string, string> = {
  recorded_at: DATE_COMPLETED_LABEL,
  program: 'Program',
  program_id: 'Program',
  organization_site_id: 'Site',
  site_id: 'Site',
  expires_at: 'Expiration Date',
  // Rails' catch-all key for a record-level validation.
  base: 'This document',
};

/** "recorded_at" → "Date Completed"; "some_other_field" → "Some Other Field". */
export function humanizeFieldName(field: string): string {
  return FIELD_LABELS[field] ?? titleize(field.replace(/_id$/, ''));
}

/**
 * Rails' `full_messages` prefixes the humanized attribute ("Recorded at cannot
 * be…") rather than the raw name; /finalize returns that shape. Swap the prefix
 * for the same label the attribute-map shape gets, so one field has one name
 * however the server chose to phrase the refusal.
 */
export function relabelRailsFullMessage(message: string): string {
  for (const [field, label] of Object.entries(FIELD_LABELS)) {
    const humanized = field.replace(/_id$/, '').replace(/_/g, ' ');
    const rails = humanized.charAt(0).toUpperCase() + humanized.slice(1);
    if (message.startsWith(`${rails} `)) return `${label} ${message.slice(rails.length + 1)}`;
  }
  return message;
}

/**
 * The date part of an ISO timestamp, as "YYYY-MM-DD".
 *
 * Read off the string rather than through `new Date(...)`: the latter resolves
 * in whatever zone the device is set to, so the same document would show a
 * different day on two phones — the opposite of the parity this helper exists
 * for. (Rails resolves its `to_date` in the app zone, so a timestamp near
 * midnight can still differ by a day from the online editor; taking the
 * timestamp's own date at least makes GO deterministic.)
 */
function toDateOnly(value: string): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return match ? match[1] : null;
}

/**
 * What the Date Completed input shows. Mirrors the online editor's
 * `date_completed_value` (`react_super_form_component.rb`):
 * `recorded_at || completed_at&.to_date || created_at&.to_date`. Only
 * `recorded_at` is ever persisted — the other two are a display default, so a
 * document the user never dated still reads the same in both apps.
 */
export function dateCompletedValue(
  doc: { recorded_at?: string | null; completed_at?: string | null; created_at?: string | null } | null | undefined
): string | null {
  if (!doc) return null;
  if (doc.recorded_at) return doc.recorded_at;
  if (doc.completed_at) return toDateOnly(doc.completed_at);
  if (doc.created_at) return toDateOnly(doc.created_at);
  return null;
}
